import React from 'react';
import { AlertTriangle, Info, Eye, Pause } from 'lucide-react';
import { Badge } from '../components/ui';
import TableDropdown from '../components/ui/TableDropdown';
import ProjectStatusCell from '../components/ProjectStatusCell';
import FormattedDateInput from '../components/ui/FormattedDateInput';
import DiscrepancyCell from '../components/DiscrepancyCell';
import {
  formatProjectedDate,
  calculateBusinessDays,
} from '../utils/dateFormatting';
import { formatJobRevNumber } from '../utils/projectFormatting';

/**
 * Project Table Column Definitions
 *
 * Standardized column definitions for the projects table using the ContactsTable format.
 * Each column definition includes:
 * - id: unique column identifier
 * - label: column header text
 * - field: data field key in project object
 * - width/minWidth/maxWidth: column sizing
 * - contentType: 'text', 'badge', 'icon', 'link', 'dropdown' - affects auto-resize
 * - sortable/searchable/resizable: feature flags
 * - render: custom cell renderer function
 */

/**
 * Create column definitions with access to runtime props
 * @param {Object} props - Runtime properties
 * @param {Function} props.onUpdateProject - Update handler
 * @param {Function} props.onRowClick - Row click handler
 * @param {Array} props.clients - Available clients
 * @param {Array} props.employees - Available employees
 * @param {Array} props.filteredEmployees - Filtered employee list
 * @param {Set} props.updatingProjects - Set of updating project keys
 * @param {Function} props.handleContactInfoClick - Contact info click handler
 * @param {Function} props.handleDiscrepancyClick - Discrepancy click handler
 * @param {Object} props.discrepanciesByProject - Discrepancies by project ID
 * @returns {Array} Column definitions
 */
export const createProjectColumns = (props) => {
  const {
    onUpdateProject,
    onRowClick,
    filteredEmployees,
    updatingProjects,
    handleContactInfoClick,
    handleDiscrepancyClick,
    discrepanciesByProject,
    loadingContacts,
    contactsByProjectId,
  } = props;

  const columns = [
    // View Column (Priority, Soft Hold, Info, Subitems)
    {
      id: 'view',
      label: '',
      field: 'id',
      width: 160,
      minWidth: 160,
      maxWidth: 160,
      sortable: false,
      searchable: false,
      resizable: false,
      align: 'center',
      contentType: 'icon',
      render: (value, project) => {
        const isOnHold = project.modelingState === 'Hold';

        return (
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
            {/* Priority Toggle - hide for on-hold projects */}
            {!isOnHold && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateProject(project.id, 'isHighPriority', !project.isHighPriority);
                }}
                style={{
                  padding: '4px',
                  background: 'none',
                  border: 'none',
                  color: project.isHighPriority ? '#ef4444' : 'var(--gray-400)',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s, color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                aria-label={`Toggle high priority for ${project.projectName}`}
                title={project.isHighPriority ? 'Remove high priority' : 'Set as high priority'}
              >
                <AlertTriangle
                  size={20}
                  fill={project.isHighPriority ? '#ef4444' : 'none'}
                  color={project.isHighPriority ? 'white' : '#9CA3AF'}
                  strokeWidth={project.isHighPriority ? 2.5 : 2}
                />
              </button>
            )}

            {/* Paused Toggle - hide for on-hold projects */}
            {!isOnHold && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateProject(project.id, 'isSoftHold', !project.isSoftHold);
                }}
                style={{
                  padding: '4px',
                  background: 'none',
                  border: 'none',
                  color: project.isSoftHold ? '#eab308' : 'var(--gray-400)',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s, color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                aria-label={`Toggle paused for ${project.projectName}`}
                title={project.isSoftHold ? 'Unpause' : 'Pause'}
              >
                <Pause
                  size={20}
                  fill={project.isSoftHold ? '#eab308' : 'none'}
                  color={project.isSoftHold ? '#eab308' : '#9CA3AF'}
                  strokeWidth={project.isSoftHold ? 2.5 : 2}
                />
              </button>
            )}

            {/* Info Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                // Trigger project details modal - will be handled by parent
                if (onRowClick) onRowClick(project, 'details');
              }}
              style={{
                padding: '8px',
                background: 'none',
                border: 'none',
                color: 'var(--gray-400)',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                minWidth: '36px',
                minHeight: '36px',
                maxWidth: '36px',
                maxHeight: '36px',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--gray-800)';
                e.currentTarget.style.color = 'var(--cta-blue)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--gray-400)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title="View project details"
              aria-label={`View project info for ${project.projectName}`}
            >
              <Info size={20} />
            </button>

            {/* Subitems Button - hide for on-hold projects */}
            {!isOnHold && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onRowClick) onRowClick(project, 'subitems');
                }}
                style={{
                  padding: '8px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--gray-400)',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  minWidth: '36px',
                  minHeight: '36px',
                  maxWidth: '36px',
                  maxHeight: '36px',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--gray-800)';
                  e.currentTarget.style.color = 'var(--cta-blue)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--gray-400)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title="View subitems"
                aria-label={`View subitems for ${project.projectName}`}
              >
                <Eye size={20} />
              </button>
            )}
          </div>
        );
      },
    },

    // Job Number
    {
      id: 'jobNumber',
      label: 'Job Number',
      field: 'jobNumber',
      width: 120,
      minWidth: 80,
      maxWidth: 180,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      formatValue: (value, row) => {
        // Include revision in measurement for auto-resize
        if (row.rev && parseInt(row.rev) > 0) {
          return `${value || ''} Rev${row.rev}`;
        }
        return value || '';
      },
      render: (value, project) => {
        const jobNumberDisplay = formatJobRevNumber(value, project.rev, value || '');
        return (
          <span
            style={{ cursor: 'pointer' }}
            onClick={() => onRowClick && onRowClick(project, 'subitems')}
          >
            {jobNumberDisplay || '—'}
          </span>
        );
      },
    },

    // Project Name
    {
      id: 'project',
      label: 'Project',
      field: 'projectName',
      width: 250,
      minWidth: 150,
      maxWidth: 400,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value, project) => (
        <span
          style={{ cursor: 'pointer', fontWeight: 500 }}
          onClick={() => onRowClick && onRowClick(project, 'subitems')}
        >
          {value || '—'}
        </span>
      ),
    },

    // Client
    {
      id: 'client',
      label: 'Client',
      field: 'clientName',
      width: 200,
      minWidth: 150,
      maxWidth: 350,
      sortable: false,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value, project) => (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => onRowClick && onRowClick(project, 'subitems')}
        >
          {value || 'Unassigned'}
        </span>
      ),
    },

    // Ready-For (Employee)
    {
      id: 'readyFor',
      label: 'Ready-For',
      field: 'readyForEmployeeId',
      width: 130,
      minWidth: 100,
      maxWidth: 200,
      sortable: true,
      resizable: true,
      contentType: 'dropdown',
      render: (value, project) => {
        const updateKey = `${project.id}-readyForEmployeeId`;
        const isSaving = updatingProjects.has(updateKey);

        const readyForOptions = [
          { value: '', label: 'Unassigned' },
          ...(filteredEmployees || []).map((employee) => ({
            value: employee.id,
            label: employee.name,
          })),
        ];

        return (
          <div style={{ position: 'relative' }}>
            <TableDropdown
              value={project.readyForEmployeeId || ''}
              options={readyForOptions}
              onChange={(newValue) => {
                const numValue = newValue ? parseInt(newValue, 10) : null;
                onUpdateProject(project.id, 'readyForEmployeeId', numValue);
              }}
              placeholder="Unassigned"
              disabled={isSaving}
              aria-label="Ready for employee"
            />
            {isSaving && <span style={{ marginLeft: '4px' }}>💾</span>}
          </div>
        );
      },
    },

    // Status
    {
      id: 'status',
      label: 'Status',
      field: 'stageStatus',
      width: 110,
      minWidth: 90,
      maxWidth: 150,
      sortable: true,
      resizable: true,
      contentType: 'badge',
      render: (value, project) => (
        <ProjectStatusCell
          status={project.stageStatus}
          dueDate={project.dueDate}
          lastStageChangeDate={project.lastStageChangeDate}
        />
      ),
    },

    // Due Date
    {
      id: 'dueDate',
      label: 'Due Date',
      field: 'dueDate',
      width: 140,
      minWidth: 120,
      maxWidth: 200,
      sortable: true,
      resizable: true,
      contentType: 'date',
      render: (value, project) => {
        const updateKey = `${project.id}-dueDate`;
        const isSaving = updatingProjects.has(updateKey);

        return (
          <div style={{ position: 'relative' }}>
            <FormattedDateInput
              value={project.dueDate || ''}
              onChange={(newValue) => onUpdateProject(project.id, 'dueDate', newValue)}
              className="date-input"
              disabled={isSaving}
            />
          </div>
        );
      },
    },

    // Projected Completion
    {
      id: 'finalProjectedCompletionDate',
      label: 'Projected Completion',
      field: 'finalProjectedCompletionDate',
      width: 140,
      minWidth: 100,
      maxWidth: 140,
      sortable: true,
      resizable: true,
      contentType: 'date',
      formatValue: (value, project) => {
        // Return the same text that will be displayed for accurate auto-resize
        const shouldShowDate =
          project.modelingState !== 'Hold' &&
          project.modelingStatus !== 'Completed';

        if (shouldShowDate && project.finalProjectedCompletionDate) {
          return formatProjectedDate(project.finalProjectedCompletionDate);
        } else if (project.modelingState === 'Hold') {
          return 'On Hold';
        } else if (project.modelingStatus === 'Completed') {
          return '—';
        } else {
          return '';
        }
      },
      render: (value, project) => {
        const shouldShowDate =
          project.modelingState !== 'Hold' &&
          project.modelingStatus !== 'Completed';

        const projectedDate =
          shouldShowDate && project.finalProjectedCompletionDate
            ? formatProjectedDate(project.finalProjectedCompletionDate)
            : project.modelingState === 'Hold'
              ? 'On Hold'
              : project.modelingStatus === 'Completed'
                ? '—'
                : '';

        return <span>{projectedDate}</span>;
      },
    },

    // Float
    {
      id: 'float',
      label: 'Float',
      field: 'float',
      width: 90,
      minWidth: 70,
      maxWidth: 120,
      sortable: true,
      resizable: true,
      align: 'center',
      contentType: 'text',
      render: (value, project) => {
        let floatDisplay = '—';
        let floatColor = 'var(--gray-400)';

        if (project.dueDate && project.finalProjectedCompletionDate) {
          const businessDays = calculateBusinessDays(
            project.finalProjectedCompletionDate,
            project.dueDate
          );

          if (businessDays !== null) {
            if (businessDays > 0) {
              floatDisplay = `+${businessDays}`;
              floatColor = '#10b981'; // Green
            } else if (businessDays < 0) {
              floatDisplay = `${businessDays}`;
              floatColor = '#ef4444'; // Red
            } else {
              floatDisplay = '0';
              floatColor = '#f59e0b'; // Orange
            }
          }
        }

        return (
          <span style={{ color: floatColor, fontWeight: 500 }}>
            {floatDisplay}
          </span>
        );
      },
    },

    // Model Builder
    {
      id: 'modelBuilder',
      label: 'Model Builder',
      field: 'modelBuilderId',
      width: 130,
      minWidth: 100,
      maxWidth: 200,
      sortable: true,
      resizable: true,
      contentType: 'dropdown',
      render: (value, project) => {
        const updateKey = `${project.id}-modelBuilderId`;
        const isSaving = updatingProjects.has(updateKey);

        const builderOptions = [
          { value: '', label: 'Unassigned' },
          ...(filteredEmployees || []).map((employee) => ({
            value: employee.id,
            label: employee.name,
          })),
        ];

        return (
          <div style={{ position: 'relative' }}>
            <TableDropdown
              value={project.modelBuilderId || ''}
              options={builderOptions}
              onChange={(newValue) => {
                const numValue = newValue ? parseInt(newValue, 10) : null;
                onUpdateProject(project.id, 'modelBuilderId', numValue);
              }}
              placeholder="Unassigned"
              disabled={isSaving}
              aria-label="Model builder"
              className="builder-dropdown"
            />
            {isSaving && <span style={{ marginLeft: '4px' }}>💾</span>}
          </div>
        );
      },
    },

    // Contact
    {
      id: 'contact',
      label: 'Contact',
      field: 'contactId',
      width: 180,
      minWidth: 150,
      maxWidth: 300,
      sortable: true,
      resizable: true,
      contentType: 'dropdown-with-button', // Has Info button next to dropdown
      formatValue: (value, project) => {
        // Use contactName from project data for auto-resize calculation
        // This ensures proper column width even before contact list loads
        return project.contactName || 'No Contact';
      },
      render: (value, project) => {
        const updateKey = `${project.id}-contactId`;
        const isSaving = updatingProjects.has(updateKey);
        const contacts = contactsByProjectId?.[project.id] || [];
        // Use the contact from the loaded list, or fall back to project.contact data
        const currentContact = contacts.find((c) => c.id === project.contactId) || project.contact;
        const isLoadingContacts = loadingContacts?.[project.id];

        // If contacts are loaded, use them. Otherwise show the current contact from project data
        const contactOptions = contacts.length > 0
          ? [
              { value: '', label: 'No Contact' },
              ...contacts.map((contact) => ({
                value: contact.id,
                label: contact.name,
              })),
            ]
          : [
              { value: '', label: 'No Contact' },
              ...(project.contact ? [{ value: project.contact.id, label: project.contact.name }] : []),
            ];

        return (
          <div
            style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '4px' }}
            onClick={(e) => e.stopPropagation()}
          >
            {currentContact && handleContactInfoClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleContactInfoClick(e, currentContact, project);
                }}
                style={{
                  padding: '8px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--gray-400)',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  minWidth: '36px',
                  minHeight: '36px',
                  maxWidth: '36px',
                  maxHeight: '36px',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--gray-800)';
                  e.currentTarget.style.color = 'var(--cta-blue)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--gray-400)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title="View contact details"
                aria-label={`View details for ${currentContact.name}`}
              >
                <Info size={20} />
              </button>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <TableDropdown
                value={project.contactId || ''}
                options={contactOptions}
                onChange={(newValue) => {
                  const numValue = newValue ? parseInt(newValue, 10) : null;
                  onUpdateProject(project.id, 'contactId', numValue);
                }}
                placeholder={isLoadingContacts ? "Loading..." : "No Contact"}
                disabled={isSaving || isLoadingContacts}
                aria-label="Contact"
              />
            </div>
            {isSaving && <span style={{ flexShrink: 0 }}>💾</span>}
          </div>
        );
      },
    },

    // Modeling State (Hold/Active)
    {
      id: 'modelingState',
      label: 'State',
      field: 'modelingState',
      width: 100,
      minWidth: 80,
      maxWidth: 120,
      sortable: true,
      resizable: true,
      align: 'center',
      contentType: 'badge',
      render: (value) => {
        if (!value) return '—';

        const variant = value === 'Hold' ? 'warning' : value === 'Active' ? 'success' : 'neutral';

        return (
          <Badge variant={variant} size="sm">
            {value}
          </Badge>
        );
      },
    },

    // Hold Reason
    {
      id: 'holdReason',
      label: 'Hold Reason',
      field: 'holdReason',
      width: 220,
      minWidth: 150,
      maxWidth: 350,
      sortable: false,
      resizable: true,
      contentType: 'text',
      render: (value) => {
        if (!value) return '—';

        // Format hold reason for display
        const reasonMap = {
          'waiting_updated_pdfs': 'Waiting on Updated PDFs',
          'waiting_updated_cad': 'Waiting on Updated CAD Files',
          'waiting_discrepancy_clarification': 'Waiting on Discrepancy Clarification',
          'client_requested_hold': 'Client Requested Hold'
        };

        const displayValue = reasonMap[value] || value;

        return (
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={displayValue}
          >
            {displayValue}
          </div>
        );
      },
    },

    // Days on Hold
    {
      id: 'daysOnHold',
      label: 'Days on Hold',
      field: 'daysOnHold',
      width: 120,
      minWidth: 100,
      maxWidth: 160,
      sortable: true,
      resizable: true,
      align: 'center',
      contentType: 'text',
      render: (value) => {
        if (!value || value === 0) return '—';

        // Color based on duration
        let color = 'var(--gray-400)';
        if (value > 30) {
          color = '#ef4444'; // Red - critical
        } else if (value > 14) {
          color = '#f59e0b'; // Orange - warning
        } else {
          color = '#10b981'; // Green - normal
        }

        return (
          <span style={{ color, fontWeight: 500 }}>
            {value} days
          </span>
        );
      },
    },

    // Hold Notes
    {
      id: 'holdNotes',
      label: 'Hold Notes',
      field: 'holdNotes',
      width: 300,
      minWidth: 150,
      maxWidth: 500,
      sortable: false,
      resizable: true,
      contentType: 'text',
      render: (value) => {
        if (!value) return '—';

        return (
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={value}
          >
            {value}
          </div>
        );
      },
    },

    // Discrepancies
    {
      id: 'discrepancies',
      label: 'Discrepancies',
      field: 'discrepancies',
      width: 160,
      minWidth: 160,
      maxWidth: 220,
      sortable: false,
      resizable: true,
      align: 'center',
      contentType: 'badge',
      render: (value, project) => {
        const discrepancies = discrepanciesByProject?.[project.id] || [];
        return (
          <DiscrepancyCell
            discrepancies={discrepancies}
            onDiscrepancyClick={handleDiscrepancyClick}
          />
        );
      },
    },

    // Ready to Invoice (only shown in Review and Ready to Export tabs)
    {
      id: 'readyToInvoice',
      label: 'Ready to Invoice',
      field: 'readyToInvoice',
      width: 140,
      minWidth: 120,
      maxWidth: 200,
      sortable: false,
      resizable: true,
      contentType: 'dropdown',
      render: (value, project) => {
        const updateKey = `${project.id}-readyToInvoice`;
        const isSaving = updatingProjects.has(updateKey);

        const readyToInvoiceOptions = [
          { value: '', label: 'Not Ready' },
          { value: 'Yes', label: 'Yes' },
          { value: 'None Needed', label: 'None Needed' },
        ];

        return (
          <div style={{ position: 'relative' }}>
            <TableDropdown
              options={readyToInvoiceOptions}
              value={project.readyToInvoice || ''}
              onChange={(newValue) =>
                onUpdateProject(project.id, 'readyToInvoice', newValue || null)
              }
              placeholder="Not Ready"
              disabled={isSaving}
            />
            {isSaving && <span style={{ marginLeft: '4px' }}>💾</span>}
          </div>
        );
      },
    },
  ];

  return columns;
};

/**
 * Get columns for a specific tab
 * Filters columns based on tab configuration
 */
export const getColumnsForTab = (tabKey, tabConfig, columnProps) => {
  const allColumns = createProjectColumns(columnProps);
  const tabColumns = tabConfig.columns || [];

  // Always include 'view' column first, then filter other columns based on tab config
  return allColumns.filter(col => {
    if (col.id === 'view') return true; // Always show view column
    return tabColumns.includes(col.id);
  });
};
